import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Flame, Search, ShieldCheck, ExternalLink, BookOpen } from "lucide-react";
import { Input } from "@/components/ui/input";

const inputCls =
  "rounded-none border-[#1F1F1F] bg-[#0A0A0A] text-white placeholder:text-[#5A5A5A] " +
  "focus-visible:ring-0 focus-visible:border-[var(--neon-cyan)] h-9 font-mono text-sm";

const FAMILY_COLORS = {
  "Brominated": "#FF3B30",
  "Chlorinated": "#FF8C00",
  "Phosphorus": "#00E5FF",
  "Nitrogen":   "#39FF14",
  "Mineral Hydroxide": "#7CFF14",
  "Boron":      "#FFCC00",
  "Silicone":   "#B0B0B0",
  "Intumescent":"#FF8C42",
  "Nanofiller": "#9D4EDD",
  "Metal Oxide":"#FFB000",
  "Phosphazene":"#00FFC8",
  "Char-former":"#FFFFFF",
  "Antimony Synergist":"#FF3B30",
  "Bio-based":  "#39FF14",
};

export default function FireRetardantsView() {
  const [data, setData] = useState({ total: 0, filtered: 0, families: [], items: [] });
  const [family, setFamily] = useState("");
  const [halogenFree, setHalogenFree] = useState("any");
  const [q, setQ] = useState("");

  useEffect(() => {
    const params = {};
    if (family) params.family = family;
    if (halogenFree !== "any") params.halogen_free = halogenFree === "yes";
    if (q) params.q = q;
    api.get("/fire-retardants", { params }).then(({ data }) => setData(data)).catch(() => {});
  }, [family, halogenFree, q]);

  const stats = useMemo(() => {
    const byFamily = {};
    data.items.forEach((x) => { byFamily[x.family] = (byFamily[x.family] || 0) + 1; });
    return byFamily;
  }, [data.items]);

  const scholarUrl = (name) =>
    `https://scholar.google.com/scholar?q=${encodeURIComponent(name + " fire retardant")}`;

  return (
    <div className="space-y-4" data-testid="fire-retardants-view">
      {/* Hero */}
      <div className="fire-retardants-hero border border-[#FF3B30] bg-gradient-to-r from-[#0A0A0A] via-[#1F0A0A] to-[#0A0A0A] p-5"
        data-testid="fr-hero">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 border border-[#FF3B30] flex items-center justify-center flex-shrink-0">
            <Flame className="w-7 h-7 text-[#FF3B30]"/>
          </div>
          <div className="flex-1">
            <div className="fr-hero-kicker font-mono text-[10px] uppercase tracking-[0.25em]">
              FIRE RETARDANTS · REFERENCE DATABASE
            </div>
            <h2 className="fr-hero-title text-2xl font-bold tracking-tight" style={{ color: '#FF0000', WebkitTextFillColor: '#FF0000', fontWeight: 800, opacity: 1, textShadow: 'none' }}>
              250+ Industrial & Academic Fire Retardants
            </h2>
            <p className="fr-refs font-mono text-[11px] leading-relaxed mt-1">
              Curated from Levchik &amp; Weil reviews · Hull (2011) · Bourbigot &amp; Duquesne (2007) ·
              Morgan &amp; Wilkie (2014). Halogen-free preferences highlighted per EU REACH / RoHS.
            </p>
            <div className="fire-retardants-stats mt-2 flex flex-wrap gap-3 font-mono text-[10px] uppercase tracking-wider">
              <span className="fr-stat-total">TOTAL <span className="font-bold">{data.total}</span></span>
              <span className="fr-stat-shown">SHOWN <span className="font-bold">{data.filtered}</span></span>
              <span className="fr-stat-families">FAMILIES <span className="font-bold">{data.families.length}</span></span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-[#1F1F1F] bg-[#0A0A0A] p-3" data-testid="fr-filters">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="md:col-span-5">
            <label className="font-mono text-[10px] uppercase tracking-wider text-[#8B8B95] block mb-1">
              Search · name / formula / polymer
            </label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#5A5A5A]"/>
              <Input className={inputCls + " pl-7"} placeholder="e.g. DOPO, APP, zinc borate, PA66..."
                value={q} onChange={(e) => setQ(e.target.value)} data-testid="fr-search"/>
            </div>
          </div>
          <div className="md:col-span-4">
            <label className="font-mono text-[10px] uppercase tracking-wider text-[#8B8B95] block mb-1">
              Family
            </label>
            <select value={family} onChange={(e) => setFamily(e.target.value)}
              className="w-full h-9 px-2 bg-[#0A0A0A] border border-[#1F1F1F] text-white font-mono text-sm focus:outline-none focus:border-[var(--neon-cyan)]"
              data-testid="fr-family">
              <option value="">All families</option>
              {data.families.map((f) => (
                <option key={f} value={f}>{`${f} · ${stats[f] ?? "—"}`}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="font-mono text-[10px] uppercase tracking-wider text-[#8B8B95] block mb-1">
              Halogen-free
            </label>
            <select value={halogenFree} onChange={(e) => setHalogenFree(e.target.value)}
              className="w-full h-9 px-2 bg-[#0A0A0A] border border-[#1F1F1F] text-white font-mono text-sm focus:outline-none focus:border-[var(--neon-cyan)]"
              data-testid="fr-halogen">
              <option value="any">Any</option>
              <option value="yes">Halogen-free only</option>
              <option value="no">Halogen-containing only</option>
            </select>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="fr-list-container fire-retardants-container border border-[#1F1F1F] bg-[#0A0A0A]" data-testid="fr-list">
        <div className="fr-list-header bg-[#141414] px-3 py-2 border-b border-[#1F1F1F] grid grid-cols-12 gap-2 font-mono text-[10px] uppercase tracking-wider">
          <div className="col-span-4">Name · Formula</div>
          <div className="col-span-2">Family</div>
          <div className="col-span-2">Loading %</div>
          <div className="col-span-2">Polymers</div>
          <div className="col-span-2 text-right">Refs</div>
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          {data.items.map((it) => {
            const c = FAMILY_COLORS[it.family] || "#FFFFFF";
            return (
              <div key={it.id}
                className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-[#1F1F1F] hover:bg-[#141414] transition-colors items-start"
                data-testid={`fr-row-${it.id}`}>
                <div className="col-span-4">
                  <div className="fr-compound-name text-sm font-semibold">{it.name}</div>
                  <div className="fr-compound-formula font-mono text-[10px]">{it.formula}</div>
                  <div className="fr-compound-mech font-mono text-[10px] mt-0.5 leading-tight">
                    {it.mechanism}
                  </div>
                </div>
                <div className="col-span-2">
                  <span
                    className="fr-family-badge font-mono text-[10px] uppercase tracking-wider"
                    style={{
                      color: '#FFFFFF',
                      backgroundColor: '#1B5E20',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontWeight: 700,
                      display: 'inline-block',
                      letterSpacing: '0.03em',
                      opacity: 1,
                      textShadow: 'none',
                      WebkitTextFillColor: '#FFFFFF'
                    }}>
                    {it.family}
                  </span>
                </div>
                <div className="fr-compound-loading col-span-2 font-mono text-xs">{it.loading_pct}</div>
                <div className="fr-compound-polymers col-span-2 font-mono text-[11px]">{it.target_polymers}</div>
                <div className="col-span-2 text-right">
                  <a href={scholarUrl(it.name)} target="_blank" rel="noreferrer"
                    className="fr-scholar-pill inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider transition-colors"
                    data-testid={`fr-scholar-${it.id}`}>
                    <BookOpen className="w-3 h-3"/>Scholar
                    <ExternalLink className="w-2.5 h-2.5"/>
                  </a>
                </div>
                <div className="fr-compound-effects col-span-12 font-mono text-[10px] pl-0">
                  {it.effects}
                </div>
              </div>
            );
          })}
          {data.items.length === 0 && (
            <div className="p-6 text-center font-mono text-sm text-[#8B8B95]">
              No fire retardants match your filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
